import { Link, useNavigate } from "react-router-dom";
import { Beef, ShieldCheck, MapPin, Star, TrendingUp, HandCoins } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useEffect } from "react";
import ShareButton from "@/components/ShareButton";

export default function Landing() {
  const { user } = useAuth();
  const nav = useNavigate();

  useEffect(() => { if (user) nav("/dashboard"); }, [user, nav]);

  return (
    <div className="min-h-screen grain relative">
      <header className="max-w-7xl mx-auto flex items-center justify-between px-4 sm:px-6 py-5">
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 rounded-xl bg-red-600 flex items-center justify-center glow">
            <Beef className="w-5 h-5 text-white" />
          </div>
          <span className="font-display font-extrabold text-xl">CarnesWork</span>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/portfolio" data-testid="link-landing-portfolio" className="hidden sm:inline-flex text-sm text-zinc-400 hover:text-white px-3 py-2">Como funciona</Link>
          <ShareButton
            variant="icon"
            testid="btn-share-landing"
            title="CarnesWork — Diárias de Açougue"
            text="Achou o app perfeito pra achar açougueiro na hora (ou pegar diária). Conhece:"
          />
          <Link to="/login" data-testid="link-landing-login" className="cw-btn-outline text-sm">Entrar</Link>
          <Link to="/register" data-testid="link-landing-register" className="cw-btn-primary text-sm">Cadastrar</Link>
        </div>
      </header>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 pt-10 sm:pt-20 pb-16 grid lg:grid-cols-2 gap-10 items-center relative z-10">
        <div className="anim-fade-up">
          <span className="badge-pill status-open mb-6">🥩 Novo · Marketplace de diaristas</span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-extrabold leading-[1.05] tracking-tight text-white">
            Diária no <span className="text-red-500">Açougue</span> na hora certa.
          </h1>
          <p className="mt-5 text-zinc-400 text-base sm:text-lg max-w-lg leading-relaxed">
            Comércios de carnes encontram <b className="text-white">açougueiros e balconistas</b> qualificados
            em minutos. Profissionais escolhem diárias, negociam valor e recebem no fim do expediente.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/register?role=comercio" data-testid="cta-comercio" className="cw-btn-primary inline-flex items-center gap-2">
              Sou Comércio <TrendingUp className="w-4 h-4" />
            </Link>
            <Link to="/register?role=acougueiro" data-testid="cta-acougueiro" className="cw-btn-outline inline-flex items-center gap-2">
              Sou Profissional <HandCoins className="w-4 h-4" />
            </Link>
          </div>

          <div className="mt-10 grid grid-cols-3 gap-6 max-w-md">
            <div><div className="text-2xl font-bold text-white">98%</div><div className="text-xs text-zinc-500">Diárias preenchidas</div></div>
            <div><div className="text-2xl font-bold text-red-500">4.8★</div><div className="text-xs text-zinc-500">Avaliação média</div></div>
            <div><div className="text-2xl font-bold text-amber-500">+230</div><div className="text-xs text-zinc-500">Profissionais</div></div>
          </div>
        </div>

        <div className="relative anim-fade-up">
          <div className="absolute -inset-4 bg-red-600/20 blur-3xl rounded-full" />
          <div className="relative rounded-3xl overflow-hidden border border-zinc-800 shadow-[0_20px_60px_-15px_rgba(220,38,38,0.4)]">
            <img alt="Açougueiro" src="https://images.unsplash.com/photo-1702564479148-e08ad6c546f5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHw0fHxidXRjaGVyJTIwbWFya2V0JTIwYXJ0aXNhbiUyMG1lYXQlMjBzaG9wJTIwY2hlZiUyMHdvcmtlcnxlbnwwfHx8fDE3OTAzNDY2ODR8MA&ixlib=rb-4.1.0&q=85" className="w-full h-[440px] object-cover" />
          </div>
          <div className="absolute -bottom-4 -left-4 cw-glass rounded-2xl px-4 py-3 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center">
              <Star className="w-5 h-5 text-emerald-400 fill-emerald-400" />
            </div>
            <div>
              <div className="text-sm font-semibold">Carlos · Açougueiro Ouro</div>
              <div className="text-xs text-zinc-400">R$ 220/diária · 12 concluídas</div>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16 relative z-10">
        <h2 className="text-3xl sm:text-4xl font-display font-bold mb-10 text-white">Como funciona</h2>
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { icon: MapPin, title: "Comércio posta a diária", desc: "Data, horário, endereço e valor base. Publicação em segundos." },
            { icon: HandCoins, title: "Profissional aceita ou negocia", desc: "Contra-oferta transparente. Todos veem o valor e o histórico." },
            { icon: ShieldCheck, title: "Trabalha e recebe no fim", desc: "Pagamento confirmado ao fim do expediente. Nota mútua." },
          ].map((f, i) => (
            <div key={i} className="cw-card p-6">
              <div className="w-11 h-11 rounded-xl bg-red-500/15 flex items-center justify-center mb-4">
                <f.icon className="w-5 h-5 text-red-500" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">{f.title}</h3>
              <p className="text-sm text-zinc-400 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16 relative z-10">
        <div className="cw-card p-8 sm:p-10 grid md:grid-cols-2 gap-8 items-center bg-gradient-to-br from-zinc-900 to-red-950/40">
          <div>
            <span className="badge-pill status-negotiating mb-4">💎 Programa Fidelidade</span>
            <h3 className="text-2xl sm:text-3xl font-display font-bold text-white mb-3">
              Selo de Confiança CarnesWork
            </h3>
            <p className="text-zinc-300 text-sm leading-relaxed mb-4">
              Conclua 5 diárias seguidas sem cancelamento e ganhe <b className="text-amber-400">+5% de bônus</b>
              , prioridade nas vagas e o cobiçado <b>Selo Ouro</b>. Um jeito de manter o serviço na plataforma.
            </p>
            <ul className="text-sm text-zinc-400 space-y-2">
              <li>✓ +50 pontos por diária concluída</li>
              <li>✓ +20 pontos por avaliação 4★+</li>
              <li>✓ Bronze → Prata → Ouro → Master</li>
            </ul>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {[
              { tier: "Bronze", req: "0 pts", color: "bg-orange-900/30 border-orange-800" },
              { tier: "Prata", req: "100 pts", color: "bg-zinc-700/40 border-zinc-500" },
              { tier: "Ouro", req: "300 pts", color: "bg-amber-900/40 border-amber-700" },
              { tier: "Master", req: "700 pts", color: "bg-red-900/40 border-red-700" },
            ].map(t => (
              <div key={t.tier} className={`p-4 rounded-xl border ${t.color}`}>
                <div className="font-display font-bold text-lg">{t.tier}</div>
                <div className="text-xs text-zinc-300 mt-1 font-mono">{t.req}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <footer className="max-w-7xl mx-auto px-4 sm:px-6 py-10 text-center text-xs text-zinc-500 border-t border-zinc-900 mt-10 relative z-10">
        <div className="flex items-center justify-center gap-4 mb-3">
          <Link to="/portfolio" className="hover:text-white">Como funciona</Link>
          <span className="text-zinc-700">·</span>
          <Link to="/terms" className="hover:text-white">Termos de Uso</Link>
          <span className="text-zinc-700">·</span>
          <Link to="/privacy" className="hover:text-white">Política de Privacidade (LGPD)</Link>
        </div>
        © 2026 CarnesWork · Feito para açougues, casas de carnes e supermercados do Brasil.
      </footer>
    </div>
  );
}
